<template>
  <page-outline :title="pageTitle" :accessibilityCompliant="true">
    <template slot="pageContent">
      <b-container fluid>
        <b-row>
          <b-col cols="12">
            <div class="mb-4">
              <p class="text-muted">
                {{ $t('sitemap.description') }}
              </p>
            </div>
            
            <!-- Liste des sections du plan du site -->
            <div v-for="section in menuSections" :key="section.title" class="mb-4">
              <h4 class="h5 font-weight-bold text-menu mb-3" style="color: #495057;">
                {{ $t(section.title) }}
              </h4>
              
              <div v-if="section.pages && section.pages.length > 0" class="pl-3">
                <div v-for="page in section.pages" :key="page.id || page.title" class="mb-1">
                  <a 
                    :href="page.link" 
                    class="dropdown-item-web-header mb-1 text-decoration-none d-block p-2 rounded"
                    @click.prevent="navigateToPage(page.link)"
                  >
                    <span class="subItemTextWebHeader">{{ $t(page.title) }}</span>
                  </a>
                </div>
              </div>
              
              <div v-else class="pl-3 text-muted">
                <em>{{ $t('sitemap.no_pages_in_section') }}</em>
              </div>
            </div>
            
            <!-- Pages individuelles (pas dans des sections) -->
            <div v-if="individualPages && individualPages.length > 0" class="mb-4">
              <h4 class="h5 font-weight-bold text-menu mb-3" style="color: #495057;">
                {{ $t('sitemap.other_features') }}
              </h4>
              <div class="pl-3">
                <div v-for="page in individualPages" :key="page.id || page.title" class="mb-1">
                  <a 
                    :href="page.link" 
                    class="dropdown-item-web-header mb-1 text-decoration-none d-block p-2 rounded"
                    @click.prevent="navigateToPage(page.link)"
                  >
                    <span class="subItemTextWebHeader">{{ $t(page.title) }}</span>
                  </a>
                </div>
              </div>
            </div>
            
            <!-- Message si aucune page accessible (ne devrait jamais arriver car on a toujours les pages d'authentification) -->
            <div v-if="totalAccessiblePages === 0" class="text-center mt-5">
              <h5 class="text-muted">
                Une erreur s'est produite lors du chargement du plan du site.
              </h5>
              <p class="text-muted">
                Veuillez actualiser la page ou contacter l'administrateur.
              </p>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import buttonsData from '@/pages/user/data/homepageData.json'
import {loadPage} from '@/helpers/routing'

export default {
  name: 'SitemapPage',
  components: {
    'page-outline': PageOutline
  },
  data() {
    return {
      pageTitle: this.$t('sitemap.title'),
      menu: [],
      menuLinkList: null,
      menuSections: [],
      individualPages: [],
      homepagePages: []
    }
  },
  computed: {
    totalAccessiblePages() {
      const sectionsCount = this.menuSections ? this.menuSections.reduce((total, section) => {
        return total + (section.pages ? section.pages.length : 0)
      }, 0) : 0
      
      const individualCount = this.individualPages ? this.individualPages.length : 0
      const homepageCount = this.homepagePages ? this.homepagePages.length : 0
      
      return sectionsCount + individualCount + homepageCount
    }
  },
  created() {
    // Copier la logique exacte du WebHeader.vue
    this.menuLinkList = require('@/components/menus/data/menu.json')
    this.loadSitemapData()
  },
  methods: {
    // Méthode pour recharger toutes les données du sitemap
    loadSitemapData() {
      this.setMenuItems()
      this.loadHomepageData()
      this.organizePages()
      this.addAuthenticationPages()
    },
    
    // Copier exactement les méthodes du WebHeader.vue
    isDisabledItem(item) {
      /* if (item && !item.enabled) {
        return true
      } */
      if (item && item.right_disable && item.right_disable.right) { // If item has a right value
        if (item.right_disable.right === 'rights_profile_consult') {
          return this.$engine.firstOn().user.get().id === this.$engine.firstOn().user.get()
          ? this.$engine.firstOn().rights.rightIs('rights_admin_consulter', item.right_disable.state)
          : this.$engine.firstOn().rights.rightIs(item.right_disable.right, item.right_disable.state)
        }
        // Return true if the right has the same state as the disabling state
        return this.$engine.firstOn().rights.rightIs(item.right_disable.right, item.right_disable.state)
      }
      return false
    },
    
    hasOneRight(itemList) {
      let that = this
      return itemList.filter(item => {
        return !that.isDisabledItem(item)
      }).length
    },
    
    canShowSpecific(subItem) {
      if ((subItem.link === '/rights/profiles/') && this.$engine.firstOn().user.get().type === '2') {
        return false
      }
      return true
    },
    
    setMenuItems() {
      this.menu = this.menuLinkList['linkContent']
      // compter le nombre de pages dispo
      let nbPage = 0
      let link = ''
      let that = this
      this.menu.forEach((item, index) => {
        if (item.list && item.list.length) {
          if (!item.hidden && that.hasOneRight(item.list) && !that.isDisabledItem(item)) {
            item.list.forEach((subitem, subindex) => {
              if (!subitem.hidden && subitem.enabled && that.canShowSpecific(subitem) && !that.isDisabledItem(subitem)) {
                nbPage++
                link = subitem.link
              }
            })
          }
        } else if (!item.hidden && item.enabled && !that.isDisabledItem(item)) {
          nbPage++
          link = item.link
        }
      })
      // Note: On ne fait pas de redirection automatique comme dans WebHeader
    },
    
    loadHomepageData() {
      // Si pas connecté, ne pas charger les pages de la homepage
      if (!this.isUserConnected()) {
        this.homepagePages = []
        return
      }
      
      let availableButtons = []
      let that = this
      
      if (buttonsData && buttonsData.buttons) {
        // Même logique que Homepage.vue pour filtrer les pages selon les droits
        buttonsData.buttons.forEach(item => {
          if (!item.disabled && !item.hidden && item.norm_right_SRTP && item.norm_right_HRTP && this.$engine.firstOn().user.getUserNorm()) {
            if (this.$engine.firstOn().user.getUserNorm() === '1' && this.hasRight(item.norm_right_SRTP)) {
              availableButtons.push(item)
            } else if (this.$engine.firstOn().user.getUserNorm() === '2' && this.hasRight(item.norm_right_HRTP)) {
              availableButtons.push(item)
            }
          } else if (!item.disabled && !item.hidden && this.hasRight(item.right)) {
            availableButtons.push(item)
          }
        })
      }
      
      this.homepagePages = availableButtons.filter(elem => 
        that.hasRight(elem.right) && elem.link && elem.link.trim() !== ''
      )
    },
    
    hasRight(right) {
      return this.$engine.firstOn().rights.rightIs(right, true)
    },
    
    isUserConnected() {
      try {
        const engine = this.$engine.firstOn()
        const user = engine.auth.user.get()
        const isAuthenticated = engine.auth.isAuth()
        
        // Simply check if user has identifier and is authenticated
        return !!user.identifier && isAuthenticated
      } catch (error) {
        console.error('Error checking user connection:', error)
        return false
      }
    },
    
    organizePages() {
      let that = this
      
      // Si pas connecté, ne pas afficher les pages protégées
      if (!this.isUserConnected()) {
        this.menuSections = []
        this.individualPages = []
        return
      }
      
      // Filtrer et organiser les sections avec sous-menus en appliquant la même logique que WebHeader
      this.menuSections = this.menu ? this.menu.filter(item => {
        if (item.list && item.list.length) {
          return !item.hidden && that.hasOneRight(item.list) && !that.isDisabledItem(item)
        }
        return false
      }).map(item => ({
        title: item.title,
        pages: item.list.filter(subitem => {
          return !subitem.hidden && subitem.enabled && that.canShowSpecific(subitem) && !that.isDisabledItem(subitem)
        })
      })) : []
      
      // Filtrer les pages individuelles en appliquant la même logique que WebHeader
      this.individualPages = this.menu ? this.menu.filter(item => {
        if (!item.list || item.list.length === 0) {
          return !item.hidden && item.enabled && !that.isDisabledItem(item)
        }
        return false
      }) : []
      
      // Ajouter les pages de la homepage qui ne sont pas dans le menu
      const allMenuLinks = []
      if (this.menu) {
        this.menu.forEach(item => {
          if (item.link) allMenuLinks.push(item.link)
          if (item.list) {
            item.list.forEach(subitem => {
              if (subitem.link) allMenuLinks.push(subitem.link)
            })
          }
        })
      }
      
      const uniqueHomepagePages = this.homepagePages ? this.homepagePages.filter(page => 
        !allMenuLinks.includes(page.link)
      ) : []
      
      if (uniqueHomepagePages.length > 0) {
        this.menuSections.push({
          title: 'sitemap.additional_features',
          pages: uniqueHomepagePages
        })
      }
    },
    
    navigateToPage(url) {
      // Même logique de navigation que WebHeader.vue
      this.$bus.$emit('switch-selected-menu', url)
      loadPage(this, { url: url })
    },
    
    addAuthenticationPages() {
      // Toujours ajouter les pages d'authentification qui sont accessibles
      const authPages = []
      
      // Si pas connecté ou pas d'identifiant valide, afficher seulement la page de connexion
      if (!this.isUserConnected()) {
        authPages.push({
          title: 'sitemap.login_page',
          link: '/login/'
        })
      } else {
        // Si connecté, ajouter la page de profil
        authPages.push({
          title: 'sitemap.profile_page',
          link: '/profile/'
        })
      }
      
      // Ajouter la section d'authentification
      if (authPages.length > 0) {
        this.menuSections.unshift({
          title: 'sitemap.authentication_section',
          pages: authPages
        })
      }
    }
  }
}
</script>

<style scoped>
/* Utiliser les mêmes styles que WebHeader.vue */
.dropdown-item-web-header {
  color: #495057;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item-web-header:hover {
  background-color: #f8f9fa;
  color: #16181b;
  text-decoration: none;
}

.subItemTextWebHeader {
  font-size: 0.9rem;
  font-weight: 400;
}

.text-menu {
  color: #495057;
}

.h5 {
  font-size: 1.125rem;
}
</style>